// frontend/src/components/BarChart/ProductBarChart.tsx
// REFORMULADO: barras horizontais com labels ricos (estilo sistema-producao)

import React from 'react';
import {
    BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList
} from 'recharts';

interface AggregatedProductData {
    name: string;
    totalRevenue: number;
    totalVolume: number;
}

interface ProductBarChartProps {
    byRevenue: AggregatedProductData[];
    byVolume: AggregatedProductData[];
    activeTab: 'revenue' | 'volume';
    productImages?: Record<string, string>;
    productNames?: Record<string, string>;
    onProductClick?: (name: string) => void;
    selectedProductName?: string | null;
}

const CHART_COLORS = {
    primary: '#b91c1c',
    secondary: '#1e293b',
    gradientStops: [
        { offset: '0%', color: 'var(--theme-chart-start, #dc2626)' },
        { offset: '100%', color: 'var(--theme-chart-end, #991b1b)' }
    ]
};

// Formata valor monetário compacto
const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency', currency: 'BRL',
        notation: 'compact', compactDisplay: 'short'
    }).format(value);
};

// Formata número inteiro
const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value);
};

// Wraps long text into multiple lines
const wrapText = (text: any, limit: number): string[] => {
    const str = String(text ?? '');
    if (!str) return [''];
    const words = str.split(/([ /])/);
    const lines: string[] = [];
    let currentLine = "";

    words.forEach((part: string) => {
        if (!part) return;
        if ((currentLine + part).length > limit && currentLine !== "") {
            lines.push(currentLine.trim());
            currentLine = part === " " ? "" : part;
        } else {
            currentLine += part;
        }
    });
    if (currentLine.trim()) lines.push(currentLine.trim());
    if (lines.length === 0) lines.push(str);
    return lines;
};

const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

// YAxis tick com quebra de linha (inspirado no sistema-producao)
const BarTick = (props: any) => {
    const { x, y, payload, productNames: names } = props;
    const label = names?.[payload.value] || payload.value;
    const maxLen = 18;
    const display = label.length > maxLen ? label.substring(0, maxLen - 1) + '...' : label;
    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={-8}
                y={0}
                textAnchor="end"
                fontSize={11}
                fontWeight={700}
                fill="var(--text-primary, #111827)"
                style={{ textTransform: 'uppercase', fontFamily: FONT }}
            >
                {display}
            </text>
        </g>
    );
};

// Rich label na ponta da barra (mostra valor + % + pares)
const RichBarLabel = (props: any) => {
    const { x, y, width, height, value, index, data, total } = props;
    if (value === undefined || value === null) return null;

    const item = data?.[index];
    const pct = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
    const pares = item?.totalVolume || 0;

    return (
        <text
            x={x + width + 8}
            y={y + height / 2}
            textAnchor="start"
            dominantBaseline="central"
            style={{ pointerEvents: 'none', fontFamily: FONT }}
        >
            <tspan
                fill="var(--text-primary, #111827)"
                fontSize={13}
                fontWeight={900}
            >
                {pct}%
            </tspan>
            <tspan
                dx={6}
                fill="var(--text-secondary, #6B7280)"
                fontSize={11}
                fontWeight={500}
            >
                {formatCurrency(value)} · {formatNumber(pares)} pares
            </tspan>
        </text>
    );
};




const ProductBarChart: React.FC<ProductBarChartProps> = ({ byRevenue, byVolume, activeTab, productImages = {}, productNames = {}, onProductClick, selectedProductName = null }) => {
    const currentData = activeTab === 'revenue' ? byRevenue : byVolume;

    const dataKey = activeTab === 'revenue' ? 'totalRevenue' : 'totalVolume';

    const totalValue = (currentData as any[]).reduce((acc: number, item: any) => {
        return acc + (activeTab === 'revenue' ? item.totalRevenue || 0 : item.totalVolume || 0);
    }, 0);

    // Tooltip customizado com imagem do produto
    const CustomTooltip = ({ active, payload, label }: any) => {
        if (!active || !payload?.length) return null;
        const d = payload[0].payload;
        const value = d.totalRevenue || d.totalVolume || d.current || 0;
        const volume = d.totalVolume || 0;
        const imgSrc = productImages?.[d.name];

        return (
            <div
                style={{
                    background: 'var(--bg-widget)',
                    borderRadius: 'var(--radius-base, 8px)',
                    padding: '10px 14px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    border: '1px solid var(--border-primary)',
                    fontSize: 14,
                    minWidth: 180
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    {imgSrc && (
                        <img
                            src={imgSrc}
                            alt={d.name}
                            style={{
                                width: 36,
                                height: 36,
                                borderRadius: 'var(--radius-base, 8px)',
                                objectFit: 'contain',
                                border: '1px solid var(--border-primary)',
                                background: '#ffffff',
                                padding: '2px',
                                flexShrink: 0
                            }}
                        />
                    )}
                    <p style={{ fontWeight: 800, color: 'var(--text-primary)', margin: 0, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                        {productNames?.[d.name] || label || d.name}
                    </p>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                    <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>Faturamento</span>
                    <span style={{ fontWeight: 800, color: 'var(--text-primary)' }}>{formatCurrency(value)}</span>
                </div>
                {volume > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginTop: 4 }}>
                        <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>Pares</span>
                        <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{formatNumber(volume)}</span>
                    </div>
                )}
            </div>
        );
    };

    // Margem direita para labels
    const maxRightMargin = 130;

    return (
        <><style>{`.recharts-wrapper * { outline: none !important; } svg:focus { outline: none !important; }`}</style>
        <ResponsiveContainer width="100%" height="100%">
            <BarChart
                data={currentData}
                layout="vertical"
                margin={{ top: 8, right: 150, left: 10, bottom: 8 }}
                barCategoryGap="20%"
            >
                <defs />

                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />

                <XAxis type="number" hide domain={[0, (max: number) => max * 1.45]} />

                <YAxis
                    dataKey="name"
                    type="category"
                    axisLine={false}
                    tickLine={false}
                    width={140}
                    tick={(props: any) => <BarTick {...props} productNames={productNames} />}
                />

                <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: 'rgba(0,0,0,0.03)' }}
                />

                <Bar
                    dataKey={dataKey}
                    radius={[0, 0, 0, 0]}
                    barSize={16}
                    style={{ cursor: 'pointer' }}
                >
                    {currentData.map((entry: any, index: number) => {
                        const isSelected = selectedProductName === entry.name;
                        const fill = isSelected ? 'var(--theme-primary, #b91c1c)' : 'var(--theme-accent, #1e293b)';
                        return (
                            <Cell
                                key={`cell-${index}`}
                                fill={fill}
                                onClick={() => onProductClick?.(entry.name)}
                            />
                        );
                    })}
                    <LabelList
                        dataKey={dataKey}
                        content={(props) => (
                            <RichBarLabel {...props} data={currentData} total={totalValue} />
                        )}
                    />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
        </>
    );
};

export default ProductBarChart;
